import {useQueries} from '@tanstack/react-query'
import Big from 'big.js'
import {format} from 'date-fns'

import Loading from '@/components/Loading/Loading'
import Price from '@/components/Price'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import {USD_DISPLAY_DECIMALS} from '@/constants/config'
import type {FoodProduct} from '@/lib/food/foodModel'
import {getFoodProductDetails} from '@/lib/food/foodService'
import {useFoodOrder} from '@/lib/food/hooks/useFoodOrder'
import HeadTags from '@/lib/head/HeadTags'
import {FoodOrderRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import NotFound from '@/views/NotFound/NotFound'

import style from './FoodOrderDetails.module.scss'
import MobileItemLayout from './shared/MobileItemLayout'
import type {Customization, FoodItem} from './shared/types'
import {getCustomizationText} from './shared/utils'

// Render single food product with image and info for details page
function FoodItemDetails({ item, productDetails }: { 
  item: FoodItem
  productDetails?: FoodProduct | null 
}) {
  const imageUrl: string | null | undefined = productDetails?.image ?? productDetails?.thumbnail_image
  const customizations: string = getCustomizationText((item.customizations ?? []) as Customization[])
  
  return (
    <div className={style.ItemDetails}>
      <div className={style.ItemImage}>
        {imageUrl ? (
          <img 
            src={imageUrl} 
            alt={item.name}
            loading="lazy"
            className={style.ItemImageImg}
          />
        ) : (
          <div className={style.ItemImagePlaceholder} aria-hidden="true">🍽️</div>
        )}
      </div>
      <div className={style.ItemInfo}>
        <h4>{item.name}</h4>
        <p>{item.description}</p>
        {customizations && (
          <p className={style.ItemCustomizations}>
            {customizations}
          </p>
        )}
      </div>
    </div>
  )
}

export default memo(function FoodOrderDetails() {
  const {foodOrderId} = FoodOrderRoute.useParams()
  const {data: order, isLoading} = useFoodOrder(foodOrderId)

  // Extract unique product IDs for fetching images
  const productIds = useMemo(() => {
    if (!order?.foodItems) return []
    return order.foodItems.map(item => item.productId)
  }, [order])

  // Fetch product details for food images
  const productQueries = useQueries({
    queries: productIds.map(productId => ({
      queryKey: ['food-product-details', productId],
      queryFn: async ({signal}: {signal?: AbortSignal}) => {
        try {
          return await getFoodProductDetails({foodProductId: productId, signal})
        } catch {
          return null
        }
      },
      staleTime: 1000 * 60 * 5,
      enabled: !!productId,
      retry: false,
      refetchOnWindowFocus: false,
    })),
  })
  
  // Build product details lookup map
  const productDetailsMap = useMemo(() => {
    const map = new Map<string, FoodProduct | null>()
    productIds.forEach((productId, index) => {
      const queryResult = productQueries[index]
      if (queryResult?.data) {
        map.set(productId, queryResult.data)
      }
    })
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useQueries result changes every render, but we only need to rebuild when productIds change
  }, [productIds])

  const goBack = () => {
    history.back()
  }

  if (isLoading) return <Loading label='Loading food order details...' />
  if (!order) return <NotFound />

  const total = Big(order.quote.totalWithoutTips)
  const subtotal = Big(order.quote.subtotal)
  const deliveryFee = Big(order.quote.deliveryFee)
  const serviceFee = Big(order.quote.serviceFee)
  const salesTax = Big(order.quote.salesTax)

  return (
    <>
      <HeadTags title={`Food Order #${order.id}`} />
      <PrimaryButton className={style.GoBack} onPress={goBack}>
        Go back
      </PrimaryButton>
      <div className={style.OrderDetails}>
        {/* Order Header */}
        <div className={style.OrderHeader}>
          <h1 className={style.Heading}>
            <VisuallyHidden>Food Order:</VisuallyHidden>
            Order #{order.id}
          </h1>
          <div className={style.OrderHeaderDetails}>
            <div className={style.OrderDate}>
              Placed on {format(new Date(order.createdAt), 'd MMM, yyyy \'at\' h:mm a')}
            </div>
          </div>
        </div>

        {/* Store and Delivery Info */}
        <div className={style.DeliverySection}>
          <div className={style.StoreInfo}>
            <div>
              <h3>Store</h3>
              <p><strong>{order.extras.store}</strong></p>
              <p>{order.extras.storeAddress}</p>
            </div>
          </div>
          
          <div className={style.DeliveryInfo}>
            <div>
              <h3>Delivery to</h3>
              <div>
                <div aria-label={`Delivery to ${order.shippingInfo.name} at ${order.shippingInfo.line1}, ${order.shippingInfo.city}, ${order.shippingInfo.state} ${order.shippingInfo.postalCode}, ${order.shippingInfo.country}, phone ${order.shippingInfo.phone}`}>
                  <p aria-hidden="true"><strong>{order.shippingInfo.name}</strong></p>
                  <p aria-hidden="true">{order.shippingInfo.line1}, {order.shippingInfo.city}, {order.shippingInfo.state} {order.shippingInfo.postalCode}, {order.shippingInfo.country}</p>
                  <p aria-hidden="true">{order.shippingInfo.phone}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Food Items */}
        <div className={style.ItemsSection}>
          <h3>Order Items ({order.foodItems.length} {order.foodItems.length === 1 ? 'item' : 'items'})</h3>
          <div className={style.ItemsList}>
            {order.foodItems.map((item, index) => (
              <div key={index} className={style.ItemRow}>
                {/* Mobile layout structure */}
                <MobileItemLayout
                  item={item}
                  productDetails={productDetailsMap.get(item.productId)}
                  styles={{
                    MobileItemHeader: style.MobileItemHeader,
                    MobileItemImage: style.MobileItemImage,
                    MobileItemImagePlaceholder: style.MobileItemImagePlaceholder,
                    MobileItemContent: style.MobileItemContent,
                    MobileItemNameAndQuantity: style.MobileItemNameAndQuantity,
                    MobileItemName: style.MobileItemName,
                    MobileItemQuantity: style.MobileItemQuantity,
                    MobileItemDetails: style.MobileItemDetails,
                    MobileItemPrice: style.MobileItemPrice,
                    MobileItemCustomizations: style.MobileItemCustomizations,
                  }}
                />

                {/* Desktop layout structure */}
                <FoodItemDetails 
                  item={item} 
                  productDetails={productDetailsMap.get(item.productId)} 
                />
                <div className={style.ItemQuantity}>
                  Qty: {item.quantity}
                </div>
                <div className={style.ItemPrice}>
                  <Price 
                    price={Big(item.basePrice)} 
                    options={{ fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Order Summary */}
        <div className={style.OrderSummary}>
          <h3>Order Summary</h3>
          <div className={style.DeliveryItems}>
            <div className={style.DeliveryItem}>
              <div className={style.DeliveryItemName}>
                Subtotal ({order.foodItems.length} {order.foodItems.length === 1 ? 'item' : 'items'})
                <VisuallyHidden>:</VisuallyHidden>
              </div>
              <div className={style.DeliveryItemPrice}>
                <Price price={subtotal} options={{ fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true }} />
              </div>
            </div>
            {deliveryFee.gt(0) && (
              <div className={style.DeliveryItem}>
                <div className={style.DeliveryItemName}>Delivery Fee<VisuallyHidden>:</VisuallyHidden></div>
                <div className={style.DeliveryItemPrice}>
                  <Price price={deliveryFee} options={{ fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true }} />
                </div>
              </div>
            )}
            {serviceFee.gt(0) && (
              <div className={style.DeliveryItem}>
                <div className={style.DeliveryItemName}>Service Fee<VisuallyHidden>:</VisuallyHidden></div>
                <div className={style.DeliveryItemPrice}>
                  <Price price={serviceFee} options={{ fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true }} />
                </div>
              </div>
            )}
            <div className={style.DeliveryItem}>
              <div className={style.DeliveryItemName}>Tax<VisuallyHidden>:</VisuallyHidden></div>
              <div className={style.DeliveryItemPrice}>
                <Price price={salesTax} options={{ fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true }} />
              </div>
            </div>
          </div>
        </div>

        {/* Order Total */}
        <div className={style.Total}>
          <VisuallyHidden>Total: ${total.toFixed(2)}</VisuallyHidden>
          <span aria-hidden="true">Total<VisuallyHidden>:</VisuallyHidden></span>
          <span aria-hidden="true">
            <Price price={total} options={{ fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true }} />
          </span>
        </div>

        {/* Additional Info */}
        <div className={style.AdditionalInfo}>
          <h3>Additional Information</h3>
          <div className={style.InfoRow}>
            <span aria-label={`${order.quote.deliveryTimeMin} to ${order.quote.deliveryTimeMax} minutes`}>Delivery Time</span>
            <span aria-hidden="true">{order.quote.deliveryTimeMin}-{order.quote.deliveryTimeMax} minutes</span>
          </div>
          <div className={style.InfoRow}>
            <span aria-label={order.quote.expectedTimeOfArrival}>Expected Arrival</span>
            <span aria-hidden="true">{order.quote.expectedTimeOfArrival}</span>
          </div>
          {order.moreInfo.note && (
            <div className={style.InfoRow}>
              <span>Note</span>
              <span>{order.moreInfo.note}</span>
            </div>
          )}
        </div>
      </div>
    </>
  )
}) 